import { useMemo } from 'react';
import { DEFAULT_AVATAR_CONFIG } from '../constants/default-avatar-config';
import type { AvatarPageConfig } from '../types/avatar-page-config';
import type { Messages } from '../types/message';

/**
 * Config 병합 및 성능 최적화 유틸리티
 */

/**
 * 깊은 병합을 수행하는 유틸리티 함수
 */
export function deepMerge<T>(target: T, source: Partial<T>): T {
  const result = { ...target };

  for (const key in source) {
    if (source.hasOwnProperty(key)) {
      const sourceValue = source[key];
      const targetValue = result[key];

      if (
        sourceValue !== null &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue) &&
        targetValue !== null &&
        typeof targetValue === 'object' &&
        !Array.isArray(targetValue)
      ) {
        result[key] = deepMerge(targetValue, sourceValue) as any;
      } else if (sourceValue !== undefined) {
        result[key] = sourceValue as any;
      }
    }
  }

  return result;
}

/**
 * Avatar Page Config를 기본값과 병합하는 일반 함수
 */
export const mergeAvatarConfig = (userConfig?: Partial<AvatarPageConfig>): AvatarPageConfig => {
  if (!userConfig) {
    return DEFAULT_AVATAR_CONFIG;
  }

  return deepMerge(DEFAULT_AVATAR_CONFIG, userConfig);
};

/**
 * 메시지 선택 로직 최적화
 * 모든 모드에서 통합된 메시지 히스토리를 보여주기 위해 realtimeMessages를 사용
 */
export const useOptimizedMessages = (
  chatMode: 'voice' | 'chat',
  textChatMessages: Messages,
  realtimeMessages: Messages
): Messages => {
  return useMemo(() => {
    return realtimeMessages;
  }, [realtimeMessages]);
};

/**
 * 로딩 상태 선택 최적화
 */
export const useOptimizedLoading = (
  chatMode: 'voice' | 'chat',
  textChatLoading: boolean,
  realtimeLoading: boolean
) => {
  return useMemo(() => {
    return chatMode === 'chat' ? textChatLoading : realtimeLoading;
  }, [chatMode, textChatLoading, realtimeLoading]);
};
